import { ChangeEvent, useState, memo, InputHTMLAttributes, useEffect, useMemo, Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { captureException } from "@sentry/nextjs";
import { usePathname } from "next/navigation";
import CloseIcon from "components/icons-svg/CloseIcon";
import GrMapLocationIcon from "components/icons-svg/GrMapLocationIcon";
import LocationIcon from "components/icons-svg/Location";
import NewLocation from "components/icons-svg/NewLocation";
import SearchPlaceSuggestions from "components/searchBar/SearchPlaceSuggestions";
import SuggestionListWithFallback from "components/searchBar/SuggestionListWithFallback";
import { isSpringBreakDealsRoute } from "components/templates/HomePage/SearchSection/springBreakIntent";
import { useDebounce } from "hooks/use-debounce";
import { Status, getGeoCodesByPlaceId } from "services/api/googleAPIs";
import { selectUser } from "stores/auth/authSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { markSearch, selectSearch } from "stores/search/searchSlice";
import { CurrentSearchTabType } from "stores/ui/types";
import { markSearchFlow, selectUI } from "stores/ui/uiSlice";
import { isDesktop, waitForGoogleMaps } from "utils/dom";
import { processPlaceDescription, stringifyBounds } from "utils/helper";
import { useDisclosure } from "utils/hooks/useDisclosure";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { autoCompleteAPI, iconMap, NewDataType } from "./SearchBarInput";
const SearchBarHomePageInputNewWithoutMemo = ({
  handleDateFocus,
  boxShadow = true,
  scrolled,
  desktopFallbackLabel,
  ...rest
}: {
  handleDateFocus: () => void;
  boxShadow?: boolean;
  scrolled?: boolean;
  desktopFallbackLabel?: string;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const dispatch = useAppDispatch();
  const search = useAppSelector(selectSearch);
  const user = useAppSelector(selectUser);
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  const isVacationRentalPage = pathname?.startsWith("/vacation-rentals");
  const isDetailPage = pathname?.startsWith("/detail");
  const isSpringBreakRoute = isSpringBreakDealsRoute(pathname);
  const [isLargerThan456] = useMediaQuery("(min-width: 456px)");
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const {
    isOpen,
    onClose,
    onOpen
  } = useDisclosure();
  const [init, setInit] = useState(false);
  const [address, setAddress] = useState("");
  const [addressData, setAddressData] = useState("");
  const [inputText, setInputText] = useState<string>(search?.shortName || "");
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);
  const debouncedInputText = useDebounce(inputText || "", 250);
  const handleSearchFlow = (localCurrentSearchTab: CurrentSearchTabType) => {
    dispatch(markSearchFlow({
      currentSearchTab: localCurrentSearchTab
    }));
  };
  const onFocus = () => {
    if (isOpen) return;
    onOpen();
    handleSearchFlow("location");
    void (async () => {
      const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
      mixpanelV2Client.CLICK_OPEN_SEARCH({
        pageOrigin: "home page",
        actionType: "initial"
      });
    })();
  };
  const [addressList, setAddressList] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const inputValue = useMemo(() => {
    if (init) {
      return address || inputText;
    }
    if (search.isSearchingInMap) {
      return "Map area";
    }
    return search?.longName || addressData;
  }, [address, addressData, init, inputText, search.isSearchingInMap, search?.longName]);
  const desktopSpringBreakLabel = desktopFallbackLabel && isLargerThan992 && !inputValue && !isOpen ? desktopFallbackLabel : "";
  const effectiveInputValue = desktopSpringBreakLabel || inputValue;
  const whatShouldBeOpenNext = () => {
    if ((isVacationRentalPage || isDetailPage) && !isDesktop()) {
      return;
    }
    if (!search.startDate && !search.endDate) {
      handleDateFocus();
      if (isLargerThan456) {
        // as in mobile view date picker should NOT auto-open
        dispatch(markSearchFlow({
          currentSearchTab: "dates"
        }));
      }
      return;
    }
    if (isHomePage && isDesktop()) {
      dispatch(markSearchFlow({
        currentSearchTab: null
      }));
      return;
    }
    const adCount = search?.adultCount ? search.adultCount : 0;
    const childCount = search?.childrenCount ? search.childrenCount : 0;
    if (adCount <= 0 && childCount <= 0) {
      dispatch(markSearchFlow({
        currentSearchTab: "guest"
      }));
      return;
    }
    dispatch(markSearchFlow({
      currentSearchTab: null
    }));
    return;
  };
  const onPlaceSelected = (place: NewDataType) => {
    if (window?.googleMapsLoaded === false) {
      return;
    }
    const getGeocodeFromPlaceId = async (placeId: string) => {
      const data = await getGeoCodesByPlaceId(placeId);
      return data?.data;
    };
    getGeocodeFromPlaceId(place.place_id).then(res => {
      if (res == null || res?.status !== Status.OK) {
        return;
      }
      const selectedPlace = res?.results[0];
      let bounds: string | null = null;
      if (selectedPlace?.geometry?.bounds || selectedPlace?.geometry?.viewport) {
        const temp = selectedPlace?.geometry?.viewport || selectedPlace?.geometry?.bounds;
        const ne = temp?.northeast;
        const sw = temp?.southwest;
        if (ne && sw) {
          bounds = stringifyBounds({
            se: {
              lat: sw.lat,
              lng: ne.lng
            },
            nw: {
              lat: ne.lat,
              lng: sw.lng
            },
            sw: sw,
            ne: ne
          });
        }
      }
      const lat: number = selectedPlace.geometry.location.lat;
      const lng: number = selectedPlace.geometry.location.lng;
      const placeId = selectedPlace?.place_id;
      const placeType = selectedPlace.types?.[0] || selectedPlace.geometry?.location_type;
      setAddressList([]);
      setAddressData(place.description);
      setAddress("");
      setInit(false);
      setHasLoadedSuggestions(false);
      dispatch(markSearch({
        lat,
        lng,
        placeId,
        placeType,
        shortName: processPlaceDescription(place.description || ""),
        longName: place.description,
        page: 0,
        bounds,
        isSearchingInMap: false,
        propertyConfigTypeId: null,
        isBoundaryVisible: true,
        isBoundaryAutoRemoved: false
      }));
      whatShouldBeOpenNext();
      onClose();
      return null;
    }).catch(e => {
      dispatch(markSearch({
        shortName: processPlaceDescription(place.description || ""),
        longName: place.description,
        page: 0,
        bounds: null,
        isSearchingInMap: false,
        propertyConfigTypeId: null
      }));
      whatShouldBeOpenNext();
      onClose();
      setAddress("");
      setAddressList([]);
      console.error(e);
      captureException(new Error(`Error 'getGeocode' ${JSON.stringify(e)}`));
    });

    // user identify mixpanel
    if (user && user.userId) {
      void (async () => {
        const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        if (user && user.userId) {
          mixpanelV1Client.IDENTIFY(user.userId);
          mixpanelV2Client.IDENTIFY(user.userId);
        }
      })();
    }
  };
  useEffect(() => {
    if (!debouncedInputText) {
      setAddressList([]);
      setIsLoadingSuggestions(false);
      setHasLoadedSuggestions(false);
      return;
    }
    setIsLoadingSuggestions(true);
    const displaySuggestions = function (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) {
      setIsLoadingSuggestions(false);
      setHasLoadedSuggestions(true);
      if (status != google.maps.places.PlacesServiceStatus.OK) {
        setAddressList([]);
        return;
      }
      const filteredSuggestions = predictions?.filter(suggestion => {
        return suggestion.types && suggestion.types.indexOf("restaurant") === -1 && suggestion.types.indexOf("food") === -1 && suggestion.types.indexOf("lodging") === -1 && suggestion.types.indexOf("university") === -1 && suggestion.types.indexOf("dentist") === -1 && suggestion.types.indexOf("doctor") === -1 && suggestion.types.indexOf("health") === -1 && suggestion.types.indexOf("hospital") === -1 && suggestion.types.indexOf("premise") === -1 && suggestion.types.indexOf("shopping_mall") === -1 && suggestion.types.indexOf("airport") === -1 && suggestion.types.indexOf("church") === -1 && suggestion.types.indexOf("store") === -1;
      }) || [];
      setAddressList(filteredSuggestions);
    };
    waitForGoogleMaps(() => {
      autoCompleteAPI(debouncedInputText, displaySuggestions);
    });
  }, [debouncedInputText]);
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (window?.googleMapsLoaded === false) {
      return;
    }
    setAddress(e.target.value || "");
    setInputText(e.target.value || "");
    setInit(true);
  };
  return <Popover as='div' className={`relative ${isHomePage ? "h-full" : ""}`} data-sentry-element="Popover" data-sentry-component="SearchBarHomePageInputNewWithoutMemo" data-sentry-source-file="SearchBarHomePageInputNew.tsx">
      {isHomePage ? <div className='relative h-full'>
          {/* Icon */}
          <div className={`pointer-events-none absolute left-6  transition-all duration-200 ${inputValue || currentSearchTab === "location" ? "top-3.5" : "top-1/2 -translate-y-1/2 "} `}>
            <NewLocation className='mr-2' />
          </div>

          {/* Floating Label */}
          <label id='addressinput' htmlFor='addressinput' className={` pointer-events-none absolute left-[54px] items-center font-semibold transition-all duration-300 md:flex ${inputValue || currentSearchTab === "location" ? `top-2.5 mb-1 text-xs leading-3 text-Gray-700` : `top-[17px] text-brand-dark ${scrolled ? " text-sm leading-[14px]" : " text-base !leading-4"}`}`}>
            Where to?
          </label>

          {/* Input */}
          <input id='addressinput' placeholder=' ' aria-label='search options' onFocus={onFocus} onBlur={() => {
        handleSearchFlow(null);
        // Add small delay to allow click events on suggestions to fire first
        setTimeout(() => {
          onClose();
        }, 200);
      }} autoComplete='off' name='addressinput' type='search' value={effectiveInputValue} onChange={handleInput} onClick={() => handleSearchFlow("location")} className={`google-autocomplete-places input max-w-auto h-full w-full text-ellipsis border-0 bg-white pl-[54px] pr-6 font-semibold text-brand-dark transition-all duration-200 placeholder:font-semibold placeholder:text-Gray-700 focus-visible:shadow-none ${scrolled ? "text-sm" : "text-base"} ${inputValue || currentSearchTab === "location" ? "pt-3.5" : ""}`} />

          {/* Clear Button */}
          {inputValue && <button className='absolute right-3 top-1/2 -translate-y-1/2 p-1' onClick={() => {
        dispatch(markSearch({
          lat: null,
          lng: null,
          placeId: null,
          placeType: null,
          shortName: null,
          longName: null,
          bounds: null,
          isSearchingInMap: false
        }));
        setInputText("");
        setAddress("");
        setAddressData("");
        setHasLoadedSuggestions(false);
      }}>
              <CloseIcon className='h-5 w-5' />
            </button>}
        </div> : <div className={`relative ${isHomePage ? "h-full" : ""}`}>
          <label htmlFor='addressinput' className={`absolute bottom-0 top-0 hidden items-center text-lg font-semibold text-Gray-500 md:flex ${isHomePage ? "left-5 h-auto" : "left-4 mb-1 h-[50px]"}`}>
            {isHomePage ? <NewLocation className={`mr-2 ${scrolled ? "mb-0.5" : "mb-1"}`} /> : <LocationIcon className='mr-1 text-secondary1000' />}
            <span className='sr-only'>Where to?</span>
          </label>
          <input aria-label='search options' onFocus={onFocus} onBlur={() => {
        // Add small delay to allow click events on suggestions to fire first
        setTimeout(() => {
          onClose();
        }, 200);
      }} autoComplete='off' name='addressinput' type='search' value={effectiveInputValue} onChange={handleInput} onClick={() => {
        handleSearchFlow("location");
      }} className={isHomePage ? `google-autocomplete-places input max-w-auto h-full w-full text-ellipsis border-0 bg-white pl-[46px] pr-6 font-semibold leading-[100%] text-brand-dark placeholder:font-semibold placeholder:text-brand-dark focus-visible:shadow-none ${scrolled ? " text-sm placeholder:text-sm" : " text-lg placeholder:text-lg "}` : "google-autocomplete-places input max-w-auto h-[50px] w-full text-ellipsis rounded-2xl bg-white pl-9 pr-10 text-base font-medium placeholder:text-base placeholder:font-medium placeholder:text-[#4F4F4F] focus-visible:shadow-none"} placeholder='Where to?' {...rest} />
          {inputValue && <div className='absolute bottom-0 right-2.5 flex h-[50px] items-center rounded-full text-secondary1000'>
              <button className='btn h-auto min-w-0 p-1' onClick={() => {
          dispatch(markSearch({
            lat: null,
            lng: null,
            placeId: null,
            placeType: null,
            shortName: null,
            longName: null,
            bounds: null,
            isSearchingInMap: false
          }));
          setInputText("");
          setAddress("");
          setAddressData("");
        }}>
                <CloseIcon className='h-5 w-5' />
              </button>
            </div>}
        </div>}

      <Transition as={Fragment} show={isOpen} enter='transition ease-out duration-200' enterFrom='opacity-0 translate-y-1' enterTo='opacity-100 translate-y-0' leave='transition ease-in duration-150' leaveFrom='opacity-100 translate-y-0' leaveTo='opacity-0 translate-y-1' data-sentry-element="Transition" data-sentry-source-file="SearchBarHomePageInputNew.tsx">
        <Popover.Panel className={`${isHomePage && inputValue === "" && currentSearchTab === "location" ? "suggestions-pop" : ""} absolute ${isSpringBreakRoute ? "!z-[1300]" : "z-[12!important]"} mt-1 w-full min-w-[260px] overflow-hidden rounded-2xl bg-white shadow mxd:mt-2 lg:w-max ${isHomePage ? `left-[30px] ${scrolled ? "mxd:mt-4" : " mxd:mt-6"}` : ""}`} data-sentry-element="Popover.Panel" data-sentry-source-file="SearchBarHomePageInputNew.tsx">
          {inputValue === "" ? <SearchPlaceSuggestions variant={isHomePage ? "homeNew" : "home"} /> : (hasLoadedSuggestions || addressList.length > 0) && <main className={`overflow-hidden bg-white ${isHomePage ? "p-2" : ""} ${boxShadow ? "shadow" : "shadow-none"}`}>
                <SuggestionListWithFallback addressList={addressList} onSelect={onPlaceSelected} iconMap={iconMap} defaultIcon={GrMapLocationIcon} variant={isHomePage ? "homeNew" : "home"} isLoading={isLoadingSuggestions} hasLoaded={hasLoadedSuggestions} />
              </main>}
        </Popover.Panel>
      </Transition>
    </Popover>;
};
const SearchBarHomePageInputNew = memo(SearchBarHomePageInputNewWithoutMemo);
export default SearchBarHomePageInputNew;